// import React, { useState, useEffect } from 'react';
// import Sidebar from '../components/Sidebar';
// import Header from '../components/Header';
// import '../assets/css/global.css';

// const MainLayout = ({ children }) => {
//   // Initialize state based on screen width
//   // If screen < 992px, start Collapsed (true). Else start Expanded (false).
//   const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 992);

//   // Handle screen resize to auto-adjust if user rotates phone or changes window
//   useEffect(() => {
//     const handleResize = () => {
//       if (window.innerWidth < 992) {
//         setIsCollapsed(true);
//       } else {
//         setIsCollapsed(false);
//       }
//     };
//     window.addEventListener('resize', handleResize);
//     return () => window.removeEventListener('resize', handleResize);
//   }, []);

//   const toggleSidebar = () => {
//     setIsCollapsed(!isCollapsed);
//   };

//   // Close sidebar automatically when clicking a link on mobile
//   const handleMobileLinkClick = () => {
//     if (window.innerWidth < 992) {
//       setIsCollapsed(true);
//     }
//   };

//   // Calculate content margin
//   const sidebarWidth = isCollapsed ? '70px' : '260px';

//   return (
//     <div className="d-flex w-100 overflow-hidden">
//       {/* Pass the same 'isCollapsed' prop. No separate mobile prop needed anymore. */}
//       <Sidebar 
//         isCollapsed={isCollapsed} 
//         toggleMobileSidebar={handleMobileLinkClick} // Reusing this prop name for link clicks
//       />
      
//       <div 
//         className="flex-grow-1 d-flex flex-column min-vh-100 transition-all" 
//         style={{ 
//           marginLeft: sidebarWidth, 
//           width: `calc(100% - ${sidebarWidth})`,
//           transition: 'margin-left 0.3s ease, width 0.3s ease'
//         }}
//       >
//         <Header toggleSidebar={toggleSidebar} />
        
//         <main className="p-4 flex-grow-1">
//             {children}
//         </main>
//       </div>
//     </div>
//   );
// };

// export default MainLayout;


import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import AiChatWidget from '../components/AiChat/AiChatWidget';
import '../assets/css/global.css';
import Loader from '../components/Loader';
import { Navigate, useLocation } from 'react-router-dom';
import { useWorkStatus } from '../context/WorkStatusContext';
import TabBar from '../components/TabBar';
import { useTabs, TabRefreshProvider } from '../context/TabsContext';
import { HOME_PATH, findScreen } from './screens';
import { useDispatch } from 'react-redux';
import { getStatusClock, resumeSession } from '../redux/dashboard/dashboardActions';
import { resumeSessionClock, watchSessionEnd } from '../utils/sessionClock';

const MainLayout = () => {
  // Define mobile breakpoint
  const mobileBreakpoint = 992;
  
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < mobileBreakpoint);
  const [isMobile, setIsMobile] = useState(window.innerWidth < mobileBreakpoint);
  const [layoutLoading, setLayoutLoading] = useState(
    localStorage.getItem("dashboard_loading") === "true"
  )

  // const [workStatus, setWorkStatus] = useState("ClockedOut");
  // const  [statusStartedAt, setStatusStartedAt] = useState(null);

  const {workStatus, statusStartedAt, setWorkStatus, setStatusStartedAt} = useWorkStatus();

  // Open screens live as tabs; the URL says which one is active.
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { tabs, activeKey, reloadTicks, activate, closeTab, closeOthers, closeAll, reorder } = useTabs();
  const knownScreen = findScreen(pathname);

  /* The session owns the clock. Mounted here rather than in App because this
     is the tree that only exists while somebody is signed in — the login
     screen has nobody to clock. */
  useEffect(() => {
    /* /attendance/resume-session, not /clock-in.
     *
     * A refresh fires the same `pagehide` a real tab close does, so the beacon
     * writes a clock-out either way and the old pairing then spent a second
     * punch clocking back in. Two punches per refresh, against a cap of 20 a
     * day — one account had burned 14 of its 20 on that churn before the cap
     * refused a genuine clock-in.
     *
     * The resume endpoint removes the tab-close punch instead of answering it,
     * so a refresh now costs nothing and leaves no gap in the day. */
    resumeSessionClock(() =>
      dispatch(resumeSession()).unwrap().then(
        () => dispatch(getStatusClock()),
        () => {},
      ),
    );
    return watchSessionEnd();
  }, [dispatch]);

  /* Reserve the header's height.

     .header-area is `position: fixed`, so it takes up no space of its own.
     Everything after it would be pulled up underneath it — which is why the
     tab strip was invisible: it was being drawn behind an opaque header at
     z-index 1000.

     This only works because the header is pinned to `top: 0` (see header.css).
     While it had no `top` it painted at its static position, which this very
     padding then pushed down by a full header height — the reservation moving
     the thing it was reserving space for. Do not drop that `top`.

     Measured rather than hard-coded because the header wraps at narrow widths
     and is a different height on mobile; a magic number would be right at one
     breakpoint and wrong at the others. */
  const shellRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);

  /* The tab strip is measured for the same reason the header is: it is fixed
     now, so the space it occupies has to be reserved by something still in
     flow, and its height changes with the font and the window width. */
  const [tabsH, setTabsH] = useState(0);

  useEffect(() => {
    const header = shellRef.current?.querySelector(".header-area");
    if (!header) return undefined;

    const measure = () => {
      setHeaderH(header.offsetHeight || 0);
      /* Re-read each time rather than captured once: the strip is absent
         until the first tab opens, and appears without the header resizing. */
      setTabsH(shellRef.current?.querySelector(".sk-tabbar")?.offsetHeight || 0);
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(header);
    /* The shell, so the strip appearing, disappearing or growing a second row
       is noticed too — observing only the header would leave the padding
       stale the moment a tab opened. */
    if (shellRef.current) ro.observe(shellRef.current);
    return () => ro.disconnect();
  }, [isMobile, tabs.length]);


  useEffect(() => {
    const handleResize = () => {
      const isSmallScreen = window.innerWidth < mobileBreakpoint;
      setIsMobile(isSmallScreen);
      
      // Auto-collapse if screen becomes small
      if (isSmallScreen) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
  if (layoutLoading) {
    const timer = setTimeout(() => {
      localStorage.removeItem("dashboard_loading");
      setLayoutLoading(false);
    }, 800); // adjust 800-1200ms as needed

    return () => clearTimeout(timer);
  }
}, [layoutLoading]);
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleMobileLinkClick = () => {
    if (isMobile) {
      setIsCollapsed(true);
    }
  };

  // FIX: On mobile, sidebarWidth should effectively be 0 for the content's margin
  // This allows the sidebar to float ON TOP of the content instead of pushing it
  //
  // Read from the same custom properties the sidebar itself is sized by
  // (theme.css -> --sk-sidebar-w). Hardcoding the pixels here meant the CSS and
  // this margin could disagree, leaving a gap or an overlap whenever the
  // sidebar width was tuned.
  const contentMargin = isMobile
    ? '0px'
    : (isCollapsed
        ? 'var(--sk-sidebar-collapsed-w, 76px)'
        : 'var(--sk-sidebar-w, 260px)');

  console.log("MainLayout:", workStatus, statusStartedAt);

  if(layoutLoading) {
    return <Loader inline variant="dashboard"/>
  }

  return (
    <div className="d-flex w-100">
      <Sidebar 
        isCollapsed={isCollapsed} 
        toggleMobileSidebar={handleMobileLinkClick} 
      />
      
      {/* Mobile backdrop — always mounted so it can fade in/out smoothly */}
      <div
        className={`sidebar-overlay ${isMobile && !isCollapsed ? "show" : ""}`}
        onClick={() => setIsCollapsed(true)}
      />

      <div 
        ref={shellRef}
        className="flex-grow-1 d-flex flex-column min-vh-100 transition-all" 
        style={{ 
          marginLeft: contentMargin, /* Updated logic here */
          flex:1,
          minWidth:0,
          /* The space the two fixed bars — header, then tab strip — do not
             take for themselves. Both are out of flow, so nothing else
             reserves it. */
          paddingTop: headerH || tabsH ? `${headerH + tabsH}px` : undefined,
          /* Published so the tab strip can stick directly beneath the header
             without a second, hand-maintained copy of its height. The header
             wraps at narrow widths, so any hardcoded number would be right at
             one breakpoint and wrong at the others — the same reason this is
             measured rather than guessed. */
          "--sk-header-h": `${headerH || 62}px`,
          /* Published for anything that needs to sit clear of both bars. */
          "--sk-tabbar-h": `${tabsH}px`,
          transition: 'margin-left 0.32s cubic-bezier(0.4,0,0.2,1)'
        }}
      >
        <Header toggleSidebar={toggleSidebar}
        isCollapsed={isCollapsed}
        isMobile={isMobile} 
        workStatus={workStatus}
        statusStartedAt={statusStartedAt}
        onStatusChanged={(newStatus) => {
          setWorkStatus(newStatus);
          setStatusStartedAt(new Date().toISOString())
        }}/>
        
        {/* Open-screens tab strip — one tab per screen opened from the sidebar. */}
        <TabBar
          /* The strip is fixed, so it is positioned against the viewport and
             has to know how wide the sidebar currently is — exactly as the
             header does. */
          isCollapsed={isCollapsed}
          isMobile={isMobile}
          tabs={tabs}
          activeKey={activeKey}
          onActivate={activate}
          onClose={closeTab}
          onCloseOthers={closeOthers}
          onCloseAll={closeAll}
          onReorder={reorder}
        />

         {/* Each page shell owns its own top offset (see theme.css) — adding
             one here too just stacked more dead space above every screen. */}
         <main className="p-1">
          {/* A path with no screen behind it falls back to the Dashboard. */}
          {!knownScreen && <Navigate to={HOME_PATH} replace />}

          {/* Every open tab stays MOUNTED so its filters, scroll position and
              typed values survive a switch; only the active one is visible. */}
          {tabs.map((t) => {
            const s = findScreen(t.key);
            if (!s) return null;
            return (
              <div
                key={t.key}
                className="sk-tab-pane"
                style={{ display: t.key === activeKey ? "block" : "none" }}
              >
                <TabRefreshProvider
                  tick={reloadTicks[t.key] || 0}
                  isActive={t.key === activeKey}
                >
                  {s.element}
                </TabRefreshProvider>
              </div>
            );
          })}
        </main>
      </div>

      {/* Floating AI assistant — available on every authenticated page */}
      <AiChatWidget />
    </div>
  );
};

export default MainLayout;